import React, { useEffect, useState, useRef, useCallback } from 'react';
import Range from '@app/model/_common/Range';

export interface DualRangeSliderProps {
	/** Minimum possible value */
	min: number,
	/** Maximum possible value */
	max: number,
	/** Current range value (optional - will default to min/max) */
	value?: Range;
	/** Step increment for values */
	step: number;
	/** Called when range changes */
	onChange: (range: Range) => void;
	/** Whether to show number inputs */
	showInputs?: boolean;
	/** Whether to show clear button */
	showClearButton?: boolean;
	/** Whether to show range info */
	showRangeInfo?: boolean;
	/** Custom CSS classes */
	className?: string;
	/** Disabled state */
	disabled?: boolean;
}

const DualRangeSlider: React.FunctionComponent<DualRangeSliderProps> = ({
	min,
	max,
	value,
	step,
	onChange,
	showInputs = false,
	showClearButton = false,
	showRangeInfo = false,
	className = '',
	disabled = false
}) => {
	const sliderRef = useRef<HTMLDivElement>(null);
	const isDragging = useRef<'from' | 'to' | null>(null);

	// Calculate possible values based on min, max, and step
	const possibleValues = [];
	for (let i = min; i <= max; i += step) {
		possibleValues.push(i);
	}
	const valuesCount = possibleValues.length;

	// Initialize current range if not provided
	const [currentRange, setCurrentRange] = useState<Range>(
		value || { from: min, to: max }
	);

	// Update internal state when external value changes (but not during dragging)
	useEffect(() => {
		if (value && !isDragging.current && (
			value.from !== currentRange.from || 
			value.to !== currentRange.to
		)) {
			setCurrentRange(value);
		}
	}, [value, currentRange.from, currentRange.to]);
	
	const getPercentage = useCallback((val: number) => {
		if (min === max) return 0;
		return ((val - min) / (max - min)) * 100;
	}, [min, max]);

	const getValueFromPercentage = useCallback((percentage: number) => {
		const val = min + (percentage / 100) * (max - min);
		return Math.round(val / step) * step;
	}, [min, max, step]);

	const handleRangeChange = useCallback((newRange: Range) => {
		setCurrentRange(newRange);
		onChange(newRange);
	}, [onChange]);

	const handleMouseMove = useCallback((e: MouseEvent) => {
		if (!isDragging.current || !sliderRef.current || disabled) return;

		const rect = sliderRef.current.getBoundingClientRect();
		const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
		const newValue = getValueFromPercentage(percentage);

		const newRange = { ...currentRange };
		if (isDragging.current === 'from') {
			newRange.from = Math.min(newValue, currentRange.to);
		} else {
			newRange.to = Math.max(newValue, currentRange.from);
		}

		handleRangeChange(newRange);
	}, [currentRange, getValueFromPercentage, handleRangeChange, disabled]);

	const handleMouseUp = useCallback(() => {
		isDragging.current = null;
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	}, [handleMouseMove]);

	const handleMouseDown = useCallback((handle: 'from' | 'to') => (e: React.MouseEvent) => {
		if (disabled) return;
		e.preventDefault();
		isDragging.current = handle;
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	}, [disabled, handleMouseMove, handleMouseUp]);

	const handleInputChange = useCallback((type: 'from' | 'to', inputValue: string) => {
		if (disabled) return;
		
		const numValue = Number(inputValue);
		if (isNaN(numValue)) return;

		const newRange = { ...currentRange };
		if (type === 'from') {
			newRange.from = Math.max(min, Math.min(numValue, currentRange.to));
		} else {
			newRange.to = Math.min(max, Math.max(numValue, currentRange.from));
		}

		handleRangeChange(newRange);
	}, [currentRange, min, max, handleRangeChange, disabled]);

	const handleClear = useCallback(() => {
		if (disabled) return;
		handleRangeChange({ from: min, to: max });
	}, [min, max, handleRangeChange, disabled]);

	const fromPercentage = getPercentage(currentRange.from);
	const toPercentage = getPercentage(currentRange.to);
	const isFiltered = currentRange.from !== min || currentRange.to !== max;

	// Don't render if there's no meaningful range
	if (min === max) {
		return null;
	}

	return (
		<div className={`space-y-4 ${className}`}>
			{/* Range Info */}
			{showRangeInfo && (
				<div className="flex justify-between items-center text-sm text-gray-600">
					<span>Range: {min} - {max}</span>
					<span>{valuesCount} steps</span>
				</div>
			)}
			{/* Custom Dual Range Slider */}
			<div className="relative">
				<div 
					ref={sliderRef}
					className={`relative h-1 bg-gray-200 rounded-lg ${disabled ? 'opacity-50' : ''}`}
				>
					{/* Active Range Track */}
					<div 
						className="absolute h-1 bg-blue-600 rounded-lg"
						style={{
							left: `${fromPercentage}%`,
							width: `${toPercentage - fromPercentage}%`
						}}
					/>

					{/* From Handle */}
					<div 
						className={`absolute w-4 h-4 bg-white border-2 border-blue-600 rounded-full transform -translate-x-1/2 -translate-y-1/2 top-1/2 ${
							disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'
						} transition-transform`}
						style={{ left: `${fromPercentage}%` }}
						onMouseDown={handleMouseDown('from')}
					/>

					{/* To Handle */}
					<div 
						className={`absolute w-4 h-4 bg-white border-2 border-blue-600 rounded-full transform -translate-x-1/2 -translate-y-1/2 top-1/2 ${
							disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'
						} transition-transform`}
						style={{ left: `${toPercentage}%` }}
						onMouseDown={handleMouseDown('to')}
					/>
				</div>
			</div>

			{/* Number Inputs */}
			{showInputs && (
				<div className="flex justify-between items-center">
					<div className="flex items-center space-x-2">
						<span className="text-sm font-medium">From:</span>
						<input 
							type="number" 
							value={currentRange.from}
							min={min}
							max={max}
							step={step}
							disabled={disabled}
							className="w-20 px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
							onChange={(e) => handleInputChange('from', e.target.value)}
						/>
					</div>
					<div className="flex items-center space-x-2">
						<span className="text-sm font-medium">To:</span>
						<input 
							type="number" 
							value={currentRange.to}
							min={min}
							max={max}
							step={step}
							disabled={disabled}
							className="w-20 px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
							onChange={(e) => handleInputChange('to', e.target.value)}
						/>
					</div>
				</div>
			)}

			{/* Clear Filter Button */}
			{showClearButton && isFiltered && !disabled && (
				<button 
					className="text-sm text-blue-600 hover:text-blue-800 underline"
					onClick={handleClear}
				>
					Clear Filter
				</button>
			)}
		</div>
	);
};

export default DualRangeSlider;
